import {createSelector} from 'reselect';

import {isDemoInbox} from '../../helpers/modelHelpers';
import {parseTeamNamespace} from '../../helpers/models/namespaceHelpers';
import {InboxModel} from '../../models/api/inboxModel';
import {AppState} from '../appState';
import {
  getFlatInboxes,
  getUserOwnedInboxes,
  getUserTeamParticipatingInboxes,
} from '../data/inboxes/inboxesSelectors';
import {getFlatOtherActiveTeammates} from '../data/teammates/teammatesSelectors';
import {getHasCellsEmptySetupActions} from '../featureFlags/featureFlagsSelectors';
import {InboxesParameters, InboxesParametersTypesEnum} from '../panels/inboxes/parameters/inboxesParameters';

/*
 * Selectors.
 */

/** Determines whether we should simplify the personal workspace (removes drafts, sent, and expand/collapse chevron). */
export const getShouldSimplifyPersonalWorkspace = createSelector(
  [getFlatInboxes, getFlatOtherActiveTeammates, getHasCellsEmptySetupActions],
  (inboxes, otherActiveTeammates, hasCellsEmptySetupActions) => {
    // We should only simplify the personal workspace if we have `CellsEmptySetupActions` and no inboxes and no other teamamtes.
    if (hasCellsEmptySetupActions) {
      return inboxes.length < 1 && otherActiveTeammates.length < 1;
    }

    return false;
  },
);

const getParameters = (_state: AppState, props: InboxesParameters) => props;

export const makeGetIsCellsEmptySetupActionsActive = () =>
  createSelector(
    [getUserOwnedInboxes, getUserTeamParticipatingInboxes, getHasCellsEmptySetupActions, getParameters],
    (userOwnedInboxes, userTeamParticipatingInboxes, hasCellsEmptySetupActions, parameters) => {
      // Calculate isCellsEmptySetupActionsActive based on feature flag, parameters, and number of inboxes.
      const isOnTeammateAndHasNoOwnedInboxes =
        parameters.type === InboxesParametersTypesEnum.USER && userOwnedInboxes.length < 1;

      const isOnTeamAndHasNoTeamInboxes =
        parameters.type === InboxesParametersTypesEnum.TEAM &&
        !hasInboxesForTeamId(userTeamParticipatingInboxes, parameters.teamId);

      const isOnTeammates = parameters.type === InboxesParametersTypesEnum.GLOBAL;

      const currentInbox =
        parameters.type === InboxesParametersTypesEnum.INBOX &&
        parameters.inboxId &&
        userTeamParticipatingInboxes.find((i) => i.id === parameters.inboxId);

      const isOnDemoInbox = Boolean(currentInbox && isDemoInbox(currentInbox));

      return (
        hasCellsEmptySetupActions &&
        (isOnTeammateAndHasNoOwnedInboxes || isOnTeamAndHasNoTeamInboxes || isOnTeammates || isOnDemoInbox)
      );
    },
  );

/*
 * Helpers.
 */

function hasInboxesForTeamId(userTeamParticipatingInboxes: ReadonlyArray<InboxModel>, teamId: number) {
  return userTeamParticipatingInboxes.map((inbox) => parseTeamNamespace(inbox.namespace)).includes(teamId);
}
